import type { EvaluationMetricDefinition } from './shared';

export const DETECTION_STEP_METRICS: EvaluationMetricDefinition[] = [
  ...(['vlm', 'index_first_pass'] as const).flatMap(route => {
    const group = route === 'vlm' ? 'VLM Event Detection' : 'First-pass Indexing Event Detection';
    const prefix = `detector/${route}`;
    const tooltip = 'Scored only against ground truth in clips handled by this detector. Raw scores precede validation; final scores include validation. These cohorts can differ, so route scores are not a paired comparison.';
    return [
      ...(['raw_', ''] as const).flatMap(raw => (['precision', 'recall', 'f1'] as const).map(key => ({
        title: `${key === 'f1' ? 'F1' : key.charAt(0).toUpperCase() + key.slice(1)} (${raw ? 'Before' : 'After'} Validation)`,
        key: `${prefix}/${raw}${key}`, color: route === 'vlm' ? '#0E7490' : '#7C3AED', isPercentage: true, group, tooltip,
        subline: `TP: {${prefix}/${raw}tp} · FP: {${prefix}/${raw}fp} · FN: {${prefix}/${raw}fn}`,
      }))),
      { title: 'Recall Share', key: `${prefix}/raw_recall_share`, color: route === 'vlm' ? '#0E7490' : '#7C3AED', isPercentage: true, group,
        tooltip: 'Ground-truth events found by this detector before validation, divided by all ground-truth events for the query, including events in SQL-filtered clips.',
        subline: `{${prefix}/raw_tp}/{${prefix}/all_gt}` },
      { title: 'Clips Handled', key: `${prefix}/clip_share`, color: '#64748B', isPercentage: true, group,
        tooltip: 'Share of all clips handled by this detector, including SQL-filtered clips in the denominator.',
        subline: `{${prefix}/clips}/{${prefix}/all_clips}` },
      ...(route === 'vlm' ? [{ title: 'Ground-truth Events', key: `${prefix}/gt`, color: '#64748B', isPercentage: false, group, tooltip }] : []),
    ];
  }),
  ...(['precision', 'recall', 'f1'] as const).map(key => ({
    title: `Clip ${key === 'f1' ? 'F1' : key.charAt(0).toUpperCase() + key.slice(1)}`, key: `sql/${key}`,
    color: '#059669', isPercentage: true, group: 'SQL Filtering Statistics',
    tooltip: 'A positive SQL prediction means keep this clip. A positive label means the clip overlaps a ground-truth event for this query. Undefined denominators display as unavailable.',
    subline: 'TP: {sql/tp} · FP: {sql/fp} · FN: {sql/fn} · TN: {sql/tn}',
  })),
  { title: 'Event Retention', key: 'sql/event_retention', color: '#059669', isPercentage: true, group: 'SQL Filtering Statistics',
    subline: '{sql/events_kept}/{sql/events_total} ground-truth events retained',
    tooltip: 'An event is retained if any kept clip overlaps it. Each ground-truth event is counted once.' },
  ...(['events_kept', 'events_filtered', 'clips_kept', 'clips_filtered', 'fail_open_clips'] as const).map(key => ({
    title: ({ events_kept: 'Events Kept', events_filtered: 'Events Filtered Out', clips_kept: 'Clips Kept', clips_filtered: 'Clips Filtered Out', fail_open_clips: 'Clips Kept by Fail-open' })[key],
    key: `sql/${key}`, color: '#64748B', isPercentage: false, group: 'SQL Filtering Statistics',
  })),
  { title: 'Index Attempts', key: 'index/attempts', color: '#7C3AED', isPercentage: false, group: 'First-pass Indexing Event Detection' },
  { title: 'Index Shortcuts', key: 'index/shortcuts', color: '#7C3AED', isPercentage: false, group: 'First-pass Indexing Event Detection' },
  { title: 'VLM Fallbacks', key: 'index/fallbacks', color: '#7C3AED', isPercentage: false, group: 'First-pass Indexing Event Detection' },
];

export const isStepMetric = (key: string) => /^(detector\/|sql\/|index\/)/.test(key);

/** Micro-aggregate additive counts; never average per-query precision/recall. */
export function aggregateStepMetrics(metrics: Record<string, any>[]): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  for (const m of metrics) {
    for (const [key, value] of Object.entries(m)) {
      const bare = key.endsWith('/mean') ? key.slice(0, -5) : key;
      if (!isStepMetric(bare) || /\/(precision|recall|f1|raw_precision|raw_recall|raw_f1|event_retention|shortcut_rate|clip_share|raw_recall_share)$/.test(bare)
          || /\/(std|min|max)$/.test(key) || key.includes('/class/') || typeof value !== 'number') continue;
      // Trial aggregates expose /mean alongside the original spelling in some payloads.
      if (!key.endsWith('/mean') && `${key}/mean` in m) continue;
      out[bare] = (out[bare] ?? 0) + value;
    }
  }
  for (const prefix of ['sql', 'detector/vlm', 'detector/index_first_pass']) {
    if (out[`${prefix}/clips`] == null) continue;
    for (const raw of prefix === 'sql' ? [''] : ['', 'raw_']) {
      const tp = out[`${prefix}/${raw}tp`] ?? 0, fp = out[`${prefix}/${raw}fp`] ?? 0, fn = out[`${prefix}/${raw}fn`] ?? 0;
      out[`${prefix}/${raw}precision`] = tp + fp ? tp / (tp + fp) : null;
      out[`${prefix}/${raw}recall`] = tp + fn ? tp / (tp + fn) : null;
      out[`${prefix}/${raw}f1`] = 2 * tp + fp + fn ? 2 * tp / (2 * tp + fp + fn) : null;
    }
  }
  for (const route of ['vlm', 'index_first_pass']) {
    const prefix = `detector/${route}`;
    if (out[`${prefix}/all_clips`] != null) out[`${prefix}/clip_share`] = out[`${prefix}/all_clips`] ? (out[`${prefix}/clips`] ?? 0) / out[`${prefix}/all_clips`]! : null;
    if (out[`${prefix}/all_gt`] != null) out[`${prefix}/raw_recall_share`] = out[`${prefix}/all_gt`] ? (out[`${prefix}/raw_tp`] ?? 0) / out[`${prefix}/all_gt`]! : null;
  }
  if (out['sql/events_total']) out['sql/event_retention'] = (out['sql/events_kept'] ?? 0) / out['sql/events_total'];
  return out;
}
